import {
  useMemo,
  type HTMLAttributes,
  useEffect,
  useState,
  useRef,
} from 'react';
import type { InlineEditProps, InlineEditHandle } from '@affine/component';
import { InlineEdit } from '@affine/component';
import { useGuard } from '@affine/core/components/guard';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { DocService } from '@affine/core/modules/doc';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  OrganizeService,
  DocTitleUniquenessService,
} from '@affine/core/modules/organize';
import { track } from '@affine/track';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';

import * as styles from './style.css';

export interface BlockSuiteHeaderTitleProps {
  /** if set, title cannot be edited */
  inputHandleRef?: InlineEditProps['handleRef'];
  className?: string;
}

const inputAttrs = {
  'data-testid': 'title-content',
} as HTMLAttributes<HTMLInputElement>;

export const BlocksuiteHeaderTitle = (props: BlockSuiteHeaderTitleProps) => {
  const { inputHandleRef } = props;
  const workspaceService = useService(WorkspaceService);
  const isSharedMode = workspaceService.workspace.openOptions.isSharedMode;
  const docService = useService(DocService);
  const organizeService = useService(OrganizeService);
  const docTitle = useLiveData(docService.doc.record.title$);
  const docTitleUniquenessService = useService(DocTitleUniquenessService);

  const inlineEditRef = useRef<InlineEditHandle>(null);

  // 获取当前文档所在的文件夹节点
  const node = useLiveData(
    useMemo(
      () => organizeService.folderTree.folderNodeByDocId$(docService.doc.id),
      [organizeService, docService.doc.id]
    )
  );

  // 获取父节点
  const parentNode$ = useMemo(() => {
    const nodeInfo = node?.info$.value;
    const parentId = nodeInfo?.parentId;
    if (parentId) {
      return organizeService.folderTree.folderNode$(parentId);
    }
    return null;
  }, [node, organizeService.folderTree]);

  const parentNode = useMemo(() => {
    const parentNodeValue = parentNode$?.value;
    return parentNodeValue || organizeService.folderTree.rootFolder;
  }, [parentNode$, organizeService.folderTree]);

  const onChange = useAsyncCallback(
    async (v: string) => {
      const originalTitle = docTitle;
      const finalTitle =
        await docTitleUniquenessService.ensureUniqueTitleInFolder(
          docService.doc.id,
          parentNode,
          v
        );

      // 如果标题被修正，强制重置 InlineEdit 的内部状态
      if (
        finalTitle !== v ||
        (finalTitle === originalTitle && v !== originalTitle)
      ) {
        // 等待一小段时间确保 docTitle 已经更新，然后重置 InlineEdit
        setTimeout(() => {
          inlineEditRef.current?.resetValue();
        }, 0);
      }

      track.$.header.actions.renameDoc();
    },
    [docService.doc.id, docTitleUniquenessService, parentNode, docTitle]
  );

  const canEdit = useGuard('Doc_Update', docService.doc.id);

  return (
    <InlineEdit
      className={clsx(styles.title, props.className)}
      value={docTitle}
      onChange={onChange}
      editable={!isSharedMode && canEdit}
      exitible={true}
      placeholder="New page"
      data-testid="title-edit-button"
      handleRef={ref => {
        inlineEditRef.current = ref;
        if (typeof inputHandleRef === 'function') {
          inputHandleRef(ref);
        } else if (inputHandleRef) {
          inputHandleRef.current = ref;
        }
      }}
      inputAttrs={inputAttrs}
    />
  );
};
